<template>
    <main>
        <integration-connector
            v-show="showVDAB"
            :active="false"
            :chosen="prelaunched"
            :connectLabel="!vdabEnabled && hasBoughtVdab ? 'settings.integrations.waiting_for_payment' : (vdabEnabled ? 'general.configure' : 'settings.integrations.request_integration')"
            :disabled="!vdabEnabled && hasBoughtVdab"
            href="https://werkgevers.vdab.be/login"
            logo-classes="bg-vdab hover:bg-vdab-light text-white"
            translation-key="vdab"
            icon="vdab"
            @connect="vdabEnabled ? configure() : requestIntegration()"
            :extra-info="vdabEnabled && vdabSubscription && vdabSubscription.cancel ? $t('vdab.integration.expires_at', {date: cancelDate}) : ''"
        />

        <transition name="slide-fade">
            <edit-resource
                v-if="configuring && vdabEnabled"
                :form="form"
                :title="$t('vdab.popup.title')"
                @close="cancelConfigure"
                @submit="save">
                <p class="w-600 mb-32 text-body"
                   v-html="$t('vdab.popup.description').replace(/\\r\\n/gi, '<br/>')">
                </p>
                <div class="flex items-center">
                    <simple-input
                            v-model="form.vdab_id"
                            class="flex-grow mr-16"
                            placeholder="123456789"
                            type="text">
                            {{ $t('vdab.company_id') }}
                        </simple-input>
                        <simple-input
                            v-model="form.vdab_password"
                            class="flex-grow"
                            placeholder="*********"
                            type="password">
                            {{ $t('vdab.password') }}
                        </simple-input>
                    </div>

                    <h3 class="mt-60 text-body mb-24 font-semibold first-letter:uppercase mr-64"
                    >
                        {{ $t('locations.offices') }}
                        <span v-if="!live" class="text-semantic-warning italic font-normal ml-8">
                            {{ $t('vdab.only_for_live') }}
                        </span>
                    </h3>

                    <toggle
                        v-if="hasLocations"
                        v-model="form.vdab_has_multiple_ids"
                        :disabled="!live"
                        class="mb-16">
                        {{ $t('vdab.has_multiple_ids') }}
                    </toggle>

                    <div
                        v-if="hasLocations"
                        :class="[live ? 'opacity-100' : 'opacity-50']"
                        class="w-600 grid grid-cols-2 space-4"
                    >

                        <div
                            v-for="location in locations"
                            :key="location.id" class="mt-8"
                        >
                            <div class="text-body font-semibold mb-8">
                                <span v-if="location.alias[locale] && location.alias[locale].length > 0">
                                    {{ location.alias[locale] }}
                                </span>
                                <span v-else>
                                    {{ location.street }} -
                                    {{ location.postal_code + ' ' + location.city }}
                                </span>
                            </div>
                            <simple-input
                                v-model="form['location_' + location.id + '_id']"
                                :disabled="!form.vdab_has_multiple_ids || !live"
                                class="mr-16 flex-grow"
                                placeholder="123456789"
                            />
                        </div>

                    </div>

                    <loading v-else size="small"></loading>

                    <template #bottomleft v-if="vdabSubscription">
                        <simple-button look="secondary" @click="startCancelling" v-if="!vdabSubscription.cancel">
                            {{ $t('vdab.integration.cancel_integration') }}
                        </simple-button>
                        <simple-button display="warning" @click="resume" v-else>
                            {{ $t('vdab.integration.resume_integration') }}
                        </simple-button>
                    </template>
            </edit-resource>
        </transition>

        <popup-modal-confirm v-if="showRequestIntegration"
                             :confirmText="$t('vdab.integration.popup_confirmation')"
                             :message="$t('vdab.integration.popup_message',{symbol: currency.sign, symbol_name: currency.name, 'italic': $t('general.prices_excl_vat', { vat_label: vatLabelForRegion })})"
                             :title="$t('vdab.integration.popup_title')"
                             :working="performingRequest"
                             confirm-button="success"
                             show-big :toggle-text="$t('general.payment_confirmation')"
                             @close="stopRequestingIntegration"
                             @confirm="confirmRequestIntegration">
            <invoice-confirmation-details title="VDAB License" price="625"/>
        </popup-modal-confirm>

        <popup-modal-confirm v-if="cancelSubscription"
                             :working="cancellingSubscription"
                             :message="$t('vdab.integration.cancel_integration_desc')"
                             :title="$t('vdab.integration.cancel_integration_title')"
                             @close="stopCancelling"
                             @confirm="cancel"/>
    </main>
</template>

<script>
import IntegrationConnector from './IntegrationConnector.vue';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import {useMultiPostingStore} from '../../state/modules/multiPosting';
import EditResource from '../ui/EditResource.vue';
import Form from '../../etc/forms/Form';
import Input from '../forms/Input.vue';
import Toggle from '../forms/Toggle.vue';
import * as companies from '../../services/CompanyService';
import Company from '../../models/Company';
import * as locationService from '../../services/LocationService';
import Location from '../../models/Location';
import {normalizeJson} from '../../../shared/helpers';
import SvgIcon from '../ui/SvgIcon.vue';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import Loading from '../ui/Loading.vue';
import PopupModalConfirm from '../ui/PopupModalConfirm';
import * as vdab from '../../services/VdabService';
import PaidSubscription from "../../models/PaidSubscription";
import SimpleButton from "../ui/SimpleButton";
import moment from 'moment';
import * as invoices from '../../services/InvoiceService';
import Invoice from "../../models/Invoice";
import InvoiceConfirmationDetails from "../InvoiceConfirmationDetails";
import RegionLabel from "../../mixins/regionLabel";
import { getSymbol } from '../../etc/currency';

function getFields(company, locations) {
    let fields = [
        {name: 'vdab_id', value: company.vdab_id},
        {name: 'vdab_password', value: company.vdab_password},
        {name: 'vdab_has_multiple_ids', value: company.vdab_has_multiple_ids},
    ];

    for (let location of locations) {
        fields.push(
            {name: 'location_' + location.id + '_id', value: location.vdab_id},
        );
    }

    return fields;
}

export default {
    components: {
        InvoiceConfirmationDetails,
        SimpleButton,
        'integration-connector': IntegrationConnector,
        'edit-resource': EditResource,
        'simple-input': Input,
        'svg-icon': SvgIcon,
        'toggle': Toggle,
        'loading': Loading,
        PopupModalConfirm,
    },
    setup() {
        const mainStore = useMainStore();
        return {mainStore};
    },
    data: function () {
        return {
            configuring: false,
            form: this.generateForm(),
            showRequestIntegration: false,
            performingRequest: false,
            cancelSubscription: false,
            cancellingSubscription: false,
            currency: {},
        };
    },
    mixins: [
        RegionLabel,
    ],
    props: {
        locations: {
            type: Array,
            required: true,
        },
        prelaunched: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState(useMainStore, ['locale', 'currentCompany', 'currentUser']),
        hasLocations() {
            return this.locations && this.locations.length > 0;
        },
        live() {
            return this.currentCompany.vdab_live;
        },
        vdabEnabled() {
            return this.currentCompany.vdab_enabled;
        },
        vdabSubscription() {
            return useRepo(PaidSubscription)
                .where('type', 'vdab')
                .where('company_id', this.currentCompany.id)
                .where('active', true)
                .first();
        },
        hasBoughtVdab() {
            return useRepo(Invoice)
                .where('type', 'subscription')
                .where('state', ['BOOKED', 'PENDING', 'EXPIRED'])
                .where(invoice => invoice.metadata.type === "vdab")
                .first();
        },
        cancelDate() {
            if (!this.vdabSubscription)
                return;

            return moment(this.vdabSubscription.expires_at).format('DD/MM/YYYY');
        },
        showVDAB() {
            // Shows VDAB integration, if the a atleast one location is in Belgium.
            if (this.locations && this.locations.length > 0) {
                for (let i = 0; i < this.locations.length; i++) {
                    const location = this.locations[i];
                    if (location && location.country_code && location.country_code.toLowerCase() === "be") {
                        return true;
                    }
                }
            }
            return false;
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        configure() {
            if (!this.showVDAB) return;
            this.configuring = true;
            this.form = this.generateForm();
        },
        cancelConfigure() {
            this.configuring = false;
        },
        generateForm() {
            return new Form(
                getFields(this.mainStore.currentCompany, this.locations));
        },
        async save() {
            this.form.submitting = true;
            let promises = [];

            promises.push(companies.update(this.currentCompany.id, {
                data: {
                    attributes: {
                        vdab_id: this.form.data.vdab_id ? this.form.data.vdab_id : null,
                        vdab_password: this.form.data.vdab_password ? this.form.data.vdab_password : null,
                        vdab_has_multiple_ids: this.form.data.vdab_has_multiple_ids,
                    },
                },
            })
                .then(result => useRepo(Company)
                    .whereId(result.id)
                    .update(normalizeJson(result))));


            for (let location of this.locations) {

                promises.push(locationService.update(location.id, {
                    data: {
                        attributes: {
                            vdab_id: this.form.data['location_' + location.id + '_id'],
                            vdab_password: this.form.data['location_' + location.id + '_password'],
                        },
                    },
                })
                    .then(insert => useRepo(Location)
                        .whereId(location.id)
                        .update(normalizeJson(insert))));
            }

            await Promise.all(promises).then(() => this[ADD_NOTIFICATION]({
                type: 'success',
                message: this.$t('settings.saved', {subject: this.$t('vdab.title')}),
            })).catch(error => console.error(error));

            this.form.finalize();
            this.form.submitting = false;
            this.configuring = false;
        },
        requestIntegration() {
            if (!this.showVDAB) return;
            this.showRequestIntegration = true;
        },
        stopRequestingIntegration() {
            this.showRequestIntegration = false;
        },
        confirmRequestIntegration() {
            this.performingRequest = true;

            vdab.requestIntegration()
                .then(result => {
                    useRepo(Company).save(normalizeJson(result))
                })
                .then(this.fetchInvoices)
                .then(() => {
                this.showRequestIntegration = false;

                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('vdab.integration.success'),
                });

            }).finally(() => this.performingRequest = false);
        },

        startCancelling() {
            this.cancelSubscription = true;
        },
        stopCancelling() {
            this.cancelSubscription = false;
        },
        cancel() {
            this.cancellingSubscription = true;

            vdab.cancelIntegration(true).then(result => {
                return useRepo(PaidSubscription).save(normalizeJson(result))
            }).then(() => {
                this.stopCancelling();
            }).finally(() => {
                this.cancellingSubscription = false;
            })
        },
        resume() {
            vdab.cancelIntegration(false).then(result => {
                return useRepo(PaidSubscription).save(normalizeJson(result))
            })
        },
        fetchInvoices() {
            return invoices.get(this.currentCompany.id, true).then(result => {
                useRepo(Invoice).save(normalizeJson(result.data))
            });
        }
    },
    mounted() {
        if (!this.vdabEnabled) {
            // If VDAB isn't enabled let's check if we have an open invoice already for VDAB
            this.fetchInvoices();
        }
        this.currency = getSymbol();
    },
    watch: {
        prelaunched(newVal) {
            if (newVal) {
                setTimeout(this.configure, 500);
            }
        },
    },
};
</script>

<style scoped>
.slide-fade-enter-active {
    transition: all .3s ease;
}

.slide-fade-leave-active {
    transition: all .3s ease;
}

.slide-fade-enter, .slide-fade-leave-to {
    opacity: 0;
}

.slide-fade-enter {
    transform: translateY(-10px);
}

.slide-fade-leave-to {
    transform: translateY(10px);
}
</style>
